import React, { useEffect, useMemo, useRef, useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Popover, Stack, TextField, Typography } from "@mui/material";
import { ICharContact } from "store/types";

import { defaultContact } from ".";

type Mode = "create" | "edit";

interface Props {
  open: boolean;
  mode: Mode;
  initial?: ICharContact | null;
  // The form is a Popover (no dimming backdrop) anchored to the "Add Contact" button.
  anchorEl: HTMLElement | null;
  onClose: () => void;
  // Provided only when the form was opened from the "Add Contact" picker — returns to it.
  onBack?: () => void;
  onSubmit: (contact: ICharContact) => void;
}

interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  title: string;
  skill: string;
  asset_class: string;
  organization: string;
  address: string;
}

const emptyValues: FormValues = {
  first_name: "",
  last_name: "",
  email: "",
  title: "",
  skill: "",
  asset_class: "",
  organization: "",
  address: ""
};

const toValues = (contact?: ICharContact | null): FormValues => ({
  first_name: contact?.first_name ?? "",
  last_name: contact?.last_name ?? "",
  email: contact?.email ?? "",
  title: contact?.title ?? "",
  skill: contact?.skill ?? "",
  asset_class: contact?.asset_class ?? "",
  organization: contact?.firm ?? "",
  address: contact?.address ?? ""
});

export const ContactFormDialog: React.FC<Props> = ({ open, mode, initial, anchorEl, onClose, onBack, onSubmit }) => {
  const [values, setValues] = useState<FormValues>(emptyValues);
  // Freeze the presentation while open so the title/labels and the Back button don't flip/jump during
  // the close animation (the parent resets mode → "create" and onBack → undefined the moment it closes).
  const [displayMode, setDisplayMode] = useState<Mode>(mode);
  const [showBack, setShowBack] = useState(false);
  const backRef = useRef<(() => void) | undefined>(undefined);

  // Reseed the form whenever it opens (create → blank, edit → the contact's values).
  useEffect(() => {
    if (open) {
      setValues(mode === "edit" ? toValues(initial) : emptyValues);
      setDisplayMode(mode);
      setShowBack(Boolean(onBack));
      backRef.current = onBack;
    }
  }, [open, mode, initial, onBack]);

  const setField = (key: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setValues((prev) => ({ ...prev, [key]: event.target.value }));

  // Identity guard (mirrors the contacts API): need at least a name or an email.
  const isValid = useMemo(
    () => Boolean(values.first_name.trim() || values.last_name.trim() || values.email.trim()),
    [values.first_name, values.last_name, values.email]
  );

  const handleSubmit = () => {
    const base: ICharContact =
      mode === "edit" && initial ? { ...initial } : { ...defaultContact, id: crypto.randomUUID(), role: null };

    onSubmit({
      ...base,
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      email: values.email.trim(),
      title: values.title.trim(),
      firm: values.organization.trim(),
      address: values.address.trim(),
      skill: values.skill.trim() || null,
      asset_class: values.asset_class.trim() || null
    });
    onClose();
  };

  const fieldProps = {
    fullWidth: true,
    size: "small" as const,
    InputLabelProps: { shrink: true }
  };

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            flexDirection: "column",
            width: "520px",
            maxWidth: "520px",
            p: "24px",
            gap: "20px",
            borderRadius: "8px",
            boxShadow: "0px 10px 15px -3px rgba(18,18,23,0.08), 0px 4px 6px -2px rgba(18,18,23,0.05)"
          }
        }
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", gap: "6px", pb: "8px" }}>
        <Typography sx={{ flex: "1 0 0", fontSize: "18px", color: "#071f35" }}>
          {displayMode === "create" ? "New Contact" : "Edit Contact"}
        </Typography>
        <IconButton onClick={onClose} sx={{ p: "4px" }} aria-label="close">
          <CloseIcon sx={{ fontSize: "16px", color: "#071f35" }} />
        </IconButton>
      </Stack>

      {showBack && (
        <Button
          variant="text"
          onClick={() => backRef.current?.()}
          startIcon={<ChevronLeftIcon />}
          sx={{ alignSelf: "flex-start", color: "#26a6ae", textTransform: "none", borderRadius: "32px" }}
        >
          Back
        </Button>
      )}

      <Stack sx={{ gap: "20px", maxHeight: "460px", overflowY: "auto", pt: "8px" }}>
        <TextField {...fieldProps} label="First Name" value={values.first_name} onChange={setField("first_name")} />
        <TextField {...fieldProps} label="Last Name" value={values.last_name} onChange={setField("last_name")} />
        <TextField {...fieldProps} label="Email" value={values.email} onChange={setField("email")} />
        <TextField {...fieldProps} label="Title" value={values.title} onChange={setField("title")} />
        <TextField
          {...fieldProps}
          label="Skill"
          placeholder="Add Skill"
          value={values.skill}
          onChange={setField("skill")}
        />
        <TextField {...fieldProps} label="Asset Class" value={values.asset_class} onChange={setField("asset_class")} />
        <TextField
          {...fieldProps}
          label="Organization"
          placeholder="Add Organization"
          value={values.organization}
          onChange={setField("organization")}
        />
        <TextField
          {...fieldProps}
          label="Address"
          placeholder="Add address"
          value={values.address}
          onChange={setField("address")}
        />
      </Stack>

      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: "32px", textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid}
          sx={{ borderRadius: "32px", textTransform: "none", bgcolor: "#071f35", "&:hover": { bgcolor: "#0a3142" } }}
        >
          {displayMode === "create" ? "Add to Deal" : "Save"}
        </Button>
      </Stack>
    </Popover>
  );
};
